import * as React from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import useMemo from 'rc-util/lib/hooks/useMemo';
import { useBaseProps } from 'rc-select';
import Tree from 'rc-tree';
import OutlinedLoading from 'acud-icon/lib/icons/OutlinedLoading';
import Checkbox from '../../checkbox';
import LegacyContext from './LegacyContext';
import TreeSelectContext from './TreeSelectContext';
import { getAllKeys, isCheckDisabled } from './utils/valueUtil';
const HIDDEN_STYLE = {
    width: 0,
    height: 0,
    display: 'flex',
    overflow: 'hidden',
    opacity: 0,
    border: 0,
    padding: 0,
    margin: 0
};
const OptionList = (_, ref) => {
    const { prefixCls, multiple, searchValue, toggleOpen, open, notFoundContent } = useBaseProps();
    const { virtual, listHeight, listItemHeight, treeData, fieldNames, onSelect, triggerChange, dropdownMatchSelectWidth, treeExpandAction } = React.useContext(TreeSelectContext);
    const { checkable, checkedKeys, halfCheckedKeys, treeExpandedKeys, treeDefaultExpandAll, treeDefaultExpandedKeys, onTreeExpand, treeIcon, showTreeIcon, showSelectAll, switcherIcon, treeLine, treeNodeFilterProp, loadData, treeLoadedKeys, treeMotion, onTreeLoad, keyEntities } = React.useContext(LegacyContext);
    const treeRef = React.useRef();
    const memoTreeData = useMemo(() => treeData, [open, treeData], (prev, next) => next[0] && prev[1] !== next[1]);
    // ========================== Values ==========================
    const mergedCheckedKeys = React.useMemo(() => {
        if (!checkable) {
            return null;
        }
        return {
            checked: checkedKeys,
            halfChecked: halfCheckedKeys
        };
    }, [checkable, checkedKeys, halfCheckedKeys]);
    // ========================== Scroll ==========================
    React.useEffect(() => {
        var _a;
        // Single mode should scroll to current key
        if (open && !multiple && checkedKeys.length) {
            (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ key: checkedKeys[0] });
        }
    }, [open]);
    // ========================== Search ==========================
    const lowerSearchValue = String(searchValue).toLowerCase();
    const filterTreeNode = (treeNode) => {
        if (!lowerSearchValue) {
            return false;
        }
        return String(treeNode[treeNodeFilterProp]).toLowerCase().includes(lowerSearchValue);
    };
    // =========================== Keys ===========================
    const [expandedKeys, setExpandedKeys] = React.useState(treeDefaultExpandedKeys);
    const [searchExpandedKeys, setSearchExpandedKeys] = React.useState(null);
    const mergedExpandedKeys = React.useMemo(() => {
        if (treeExpandedKeys) {
            return [...treeExpandedKeys];
        }
        return searchValue ? searchExpandedKeys : expandedKeys;
    }, [expandedKeys, searchExpandedKeys, treeExpandedKeys, searchValue]);
    const allKeys = React.useMemo(() => {
        return getAllKeys(treeData, fieldNames);
    }, [treeData, fieldNames]);
    React.useEffect(() => {
        if (searchValue) {
            setSearchExpandedKeys(allKeys);
        }
    }, [searchValue]);
    const onInternalExpand = (keys) => {
        setExpandedKeys(keys);
        setSearchExpandedKeys(keys);
        if (onTreeExpand) {
            onTreeExpand(keys);
        }
    };
    // ========================== Events ==========================
    const onListMouseDown = event => {
        event.preventDefault();
    };
    const onInternalSelect = (__, info) => {
        const { node } = info;
        if (checkable && isCheckDisabled(node)) {
            return;
        }
        onSelect(node.key, {
            selected: !checkedKeys.includes(node.key)
        });
        if (!multiple) {
            toggleOpen(false);
        }
    };
    // ========================= Keyboard =========================
    const [activeKey, setActiveKey] = React.useState(null);
    const activeEntity = keyEntities[activeKey];
    React.useImperativeHandle(ref, () => {
        var _a;
        return ({
            scrollTo: (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo,
            onKeyDown: event => {
                var _a;
                const { which } = event;
                switch (which) {
                    // >>> Arrow keys
                    case KeyCode.UP:
                    case KeyCode.DOWN:
                    case KeyCode.LEFT:
                    case KeyCode.RIGHT:
                        (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.onKeyDown(event);
                        break;
                    // >>> Select item
                    case KeyCode.ENTER: {
                        if (activeEntity) {
                            const { selectable, value } = (activeEntity === null || activeEntity === void 0 ? void 0 : activeEntity.node) || {};
                            if (selectable !== false) {
                                onInternalSelect(null, {
                                    node: { key: activeKey },
                                    selected: !checkedKeys.includes(value)
                                });
                            }
                        }
                        break;
                    }
                    // >>> Close
                    case KeyCode.ESC: {
                        toggleOpen(false);
                    }
                }
            },
            onKeyUp: () => { }
        });
    });
    const treeProps = {
        fieldNames
    };
    if (treeLoadedKeys) {
        treeProps.loadedKeys = treeLoadedKeys;
    }
    if (mergedExpandedKeys) {
        treeProps.expandedKeys = mergedExpandedKeys;
    }
    const allDisabledCheckedKeys = React.useMemo(() => {
        const adcKeys = [];
        const loopTreeData = arr => {
            arr.forEach(data => {
                if (data.disabled && checkedKeys.includes(data.key)) {
                    adcKeys.push(data.key);
                }
                if (data.children && Array.isArray(data.children)) {
                    loopTreeData(data.children);
                }
            });
        };
        loopTreeData(treeData);
        return adcKeys;
    }, [treeData, checkedKeys]);
    const allAbleKeys = React.useMemo(() => {
        const abKeys = [];
        const loopTreeData = arr => {
            arr.forEach(data => {
                if (!data.disabled) {
                    abKeys.push(data.key);
                }
                if (data.children && Array.isArray(data.children)) {
                    loopTreeData(data.children);
                }
            });
        };
        loopTreeData(treeData);
        return abKeys;
    }, [treeData]);
    const allAbleCheckedKeys = React.useMemo(() => {
        return checkedKeys.filter(key => allAbleKeys.includes(key));
    }, [allAbleKeys, checkedKeys]);
    const selectAllStatus = React.useMemo(() => {
        const hasCheckedValue = allAbleCheckedKeys.length > 0;
        return {
            all: hasCheckedValue && allAbleKeys.length === allAbleCheckedKeys.length,
            indeterminate: hasCheckedValue && allAbleCheckedKeys.length !== allAbleKeys.length,
            disabled: allAbleKeys.length === 0
        };
    }, [allAbleCheckedKeys, allAbleKeys]);
    const handleCheckAll = React.useCallback(() => {
        if (selectAllStatus.all) {
            triggerChange([...allDisabledCheckedKeys]);
        }
        else {
            triggerChange([...allDisabledCheckedKeys, ...allAbleKeys]);
        }
    }, [selectAllStatus, allAbleKeys, allDisabledCheckedKeys, triggerChange]);
    const formatContent = React.useCallback((content, searchValue, prefixCls) => {
        if (typeof content !== 'string' && typeof content !== 'number') {
            return <>{content}</>;
        }
        const contentStr = content.toString();
        const lowerContent = contentStr.toLowerCase();
        const lowerSearch = searchValue.toLowerCase().trim();
        const searchStartIndex = lowerContent.indexOf(lowerSearch);
        const len = lowerSearch.length;
        // 存在搜索值不在content的情况，比如key与children不一致时
        if (searchStartIndex === -1) {
            return <>{content}</>;
        }
        return (<>
            {contentStr.slice(0, searchStartIndex)}
            <span className={`${prefixCls}-search-highlight`}>
                {contentStr.slice(searchStartIndex, searchStartIndex + len)}
            </span>
            {contentStr.slice(searchStartIndex + len)}
        </>);
    }, []);
    // 处理搜索高亮匹配，当显示字段与过滤字段相同时高亮显示”匹配片段“，反之高亮显示 "当前选项"
    const handleHighlightContent = React.useCallback((item, searchValue, prefixCls) => {
        const filterContent = item[treeNodeFilterProp];
        if (fieldNames._title.includes(treeNodeFilterProp)) {
            return Object.assign(Object.assign({}, item), { [treeNodeFilterProp]: formatContent(filterContent, searchValue, prefixCls) });
        }
        if (typeof filterContent !== 'string' && typeof filterContent !== 'number') {
            return item;
        }
        const lowerContent = filterContent.toString().toLowerCase();
        const lowerSearch = searchValue.toLowerCase().trim();
        const searchStartIndex = lowerContent.indexOf(lowerSearch);
        // 根据_title获取当前显示的字段名称，_title默认值为['title', 'label']，搜索值不匹配时，直接返回，反之高亮当前选项
        const fieldName = fieldNames._title.find(field => item.hasOwnProperty(field));
        return searchStartIndex === -1 ? item : Object.assign(Object.assign({}, item), { [fieldName]: <span className={`${prefixCls}-search-highlight`}>
                {item[fieldName]}
            </span> });
    }, [treeNodeFilterProp, fieldNames, formatContent]);
    const formatTreeData = React.useMemo(() => {
        if ((searchValue === null || searchValue === void 0 ? void 0 : searchValue.toString().trim()) === '') {
            return memoTreeData;
        }
        const loopTree = arr => {
            return arr.map(item => {
                const newItem = handleHighlightContent(item, searchValue, prefixCls);
                if (newItem.children && newItem.children.length) {
                    newItem.children = loopTree(newItem.children);
                }
                return newItem;
            });
        };
        return loopTree(memoTreeData);
    }, [memoTreeData, searchValue, prefixCls, formatContent]);
    // ========================== Render ==========================
    if (memoTreeData.length === 0) {
        return (<div role="listbox" className={`${prefixCls}-empty`} onMouseDown={onListMouseDown}>
                {notFoundContent}
            </div>);
    }
    // 渲染TreeSelect的icon
    const renderSwitcherIcon = (nodeProps) => {
        const { loading } = nodeProps;
        if (loading) {
            return <OutlinedLoading width={16} animation="spin"/>;
        }
        return typeof switcherIcon === 'function' ? switcherIcon(nodeProps) : switcherIcon;
    };
    return (<div onMouseDown={onListMouseDown}>
            {activeEntity && open && (<span style={HIDDEN_STYLE} aria-live="assertive">
                    {activeEntity.node.value}
                </span>)}
            {multiple && showSelectAll && (<div className={`${prefixCls}-tree-select-all`}>
                    <Checkbox checked={selectAllStatus.all} indeterminate={selectAllStatus.indeterminate} disabled={selectAllStatus.disabled} onClick={handleCheckAll}>全选</Checkbox>
                </div>)}
            <Tree ref={treeRef} focusable={false} prefixCls={`${prefixCls}-tree`} treeData={formatTreeData} height={listHeight} itemHeight={listItemHeight} virtual={virtual !== false && dropdownMatchSelectWidth !== false} multiple={multiple} icon={treeIcon} showIcon={showTreeIcon} switcherIcon={renderSwitcherIcon} showLine={treeLine} loadData={searchValue ? null : loadData} motion={treeMotion} activeKey={activeKey} 
    // We handle keys by out instead tree self
    checkable={checkable} checkStrictly checkedKeys={mergedCheckedKeys} selectedKeys={checkedKeys} defaultExpandAll={treeDefaultExpandAll} {...treeProps} 
    // Proxy event out
    onActiveChange={setActiveKey} onSelect={onInternalSelect} onCheck={onInternalSelect} onExpand={onInternalExpand} onLoad={onTreeLoad} filterTreeNode={filterTreeNode} expandAction={treeExpandAction}/>
        </div>);
};
const RefOptionList = React.forwardRef(OptionList);
RefOptionList.displayName = 'OptionList';
export default RefOptionList;
